import { twMerge } from 'tailwind-merge'
import { useEffect, useState } from 'react'

import { api, apiNoAuth } from '../services/api.service'

import { style } from '../style'

export default function ChatStyleMessages({ foundEvents, foundEvent, onMessageFinder, ownership, user, showOnlyReturnInstructions = false, viewerType = 'owner', refreshTrigger = 0 }) {
    const [messages, setMessages] = useState([])

    useEffect(() => {
        if (ownership?._id && !showOnlyReturnInstructions) {
            loadMessages()
        }
    }, [ownership?._id, showOnlyReturnInstructions, refreshTrigger])

    const loadMessages = async () => {
        if (!ownership?._id) return

        try {
            // Use authenticated endpoint only for owner view, public endpoint for finder view
            const response = viewerType === 'owner' && user ? await api(`${process.env.REACT_APP_API_URL}/app/messages/${ownership._id}`) : await apiNoAuth(`${process.env.REACT_APP_API_URL}/public/messages/${ownership._id}`)
            setMessages(response.messages || [])
        } catch (error) {
            console.error('Failed to load messages:', error)
        }
    }

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp)
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    }

    // Function to get the correct return instructions based on the hierarchy
    const getReturnInstructions = () => {
        // Priority: temporaryOverride (when lost) > ownership specific > user default
        return ownership?.returnInstructions || ownership?.currentOwner?.defaultReturnInstructions
    }

    // Create message array
    let allMessages = []

    if (showOnlyReturnInstructions) {
        // Only show return instructions
        const returnInstructions = getReturnInstructions()
        if (returnInstructions) {
            allMessages = [
                {
                    content: returnInstructions,
                    sender: 'owner',
                    timestamp: ownership?.lostDate || new Date(),
                    messageType: 'initial_instructions',
                },
            ]
        }
    } else {
        // Show all logged messages in chronological order
        allMessages = messages.map((msg) => ({
            content: msg.content,
            sender: msg.sender,
            timestamp: msg.timestamp,
            messageType: msg.messageType,
            location: msg.location,
        }))

        // If no logged messages but we have return instructions, add them as fallback
        if (allMessages.length === 0) {
            const returnInstructions = getReturnInstructions()
            if (returnInstructions) {
                allMessages.push({
                    content: returnInstructions,
                    sender: 'owner',
                    timestamp: ownership?.lostDate || new Date(),
                    messageType: 'initial_instructions',
                })
            }

            // Add found events as fallback
            const finderMessages = foundEvents && foundEvents.length > 0 ? foundEvents : foundEvent ? [foundEvent] : []
            finderMessages.forEach((message) => {
                allMessages.push({
                    content: message.note,
                    sender: 'finder',
                    timestamp: message.date || new Date(),
                    messageType: 'found_report',
                    location: message.location,
                })
            })
        }

        // Sort messages by timestamp
        allMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    }

    if (allMessages.length === 0) {
        return (
            <div className='text-center text-gray-500 py-4'>
                <p>No messages yet.</p>
            </div>
        )
    }

    // Determine which side messages should appear based on viewer type
    const isOwnMessage = (messageSender) => {
        // For owner view: owner messages are "own" (right side)
        // For finder view: finder messages are "own" (right side)
        return viewerType === 'owner' ? messageSender === 'owner' : messageSender === 'finder'
    }

    return (
        <div className='space-y-3'>
            {allMessages.map((message, index) => (
                <div key={index} className={twMerge('flex', isOwnMessage(message.sender) ? 'justify-end' : 'justify-start')}>
                    <div className={twMerge('max-w-xs lg:max-w-md px-3 py-2 rounded-lg text-sm', isOwnMessage(message.sender) ? 'bg-brand-500 text-white' : 'bg-gray-200 text-gray-800')}>
                        <div className='whitespace-pre-wrap'>{message.content}</div>
                        {message.location && (
                            <div className='mt-2 pt-2 border-t border-opacity-30' style={{ borderColor: isOwnMessage(message.sender) ? 'white' : '#374151' }}>
                                <div className={twMerge('text-xs font-medium mb-1', isOwnMessage(message.sender) ? 'text-brand-100' : 'text-gray-600')}>📍 Location shared</div>
                                <a href={`https://www.google.com/maps?q=${message.location.latitude},${message.location.longitude}`} target='_blank' rel='noopener noreferrer' className={twMerge('text-xs underline', isOwnMessage(message.sender) ? 'text-white hover:text-brand-100' : 'text-brand-500 hover:text-brand-600')}>
                                    View on Google Maps
                                </a>
                            </div>
                        )}
                        <div className={twMerge('text-xs mt-1', isOwnMessage(message.sender) ? 'text-brand-100' : 'text-gray-500')}>{formatTimestamp(message.timestamp)}</div>
                    </div>
                </div>
            ))}

            {!showOnlyReturnInstructions && viewerType === 'owner' && (
                <div className='mt-4'>
                    <button className={twMerge(style.button.classname, style.button._secondary.classname)} onClick={onMessageFinder}>
                        Message Finder
                    </button>
                </div>
            )}
        </div>
    )
}
